import { updateVideoPlayerVideoRecordingVersion } from 'actions/videoPlayer';
import {
  NONE,
  NUM_FILLER_WORDS_IN_VIDEO_VERSION,
  SUCCESS,
} from 'constants/videoTranscript';
import { prioritizeSignedThumbnails } from 'utilities/video';

import {
  EMOJI_CLICKED_IN_VIDEO_PLAYER,
  FETCH_VIDEO_PLAYER_VIDEO,
  REQUEST_VIDEO_PLAYER_CURRENT_TIME,
  REQUEST_VIDEO_PLAYER_ENTER_FULL_SCREEN,
  REQUEST_VIDEO_PLAYER_LEAVE_FULL_SCREEN,
  REQUEST_VIDEO_PLAYER_MUTE,
  REQUEST_VIDEO_PLAYER_PAUSE,
  REQUEST_VIDEO_PLAYER_PLAY,
  REQUEST_VIDEO_PLAYER_PLAYBACK_RATE,
  REQUEST_VIDEO_PLAYER_SEEKTO_TS,
  REQUEST_VIDEO_PLAYER_UNMUTE,
  REQUEST_VIDEO_PLAYER_VOLUME,
  SET_CURRENT_CAPTION,
  UDPATE_IS_FROM_SLACK_DESKTOP_APP,
  UPDATE_CAPTIONS_DETAILS,
  UPDATE_CAPTIONS_VISIBLE,
  UPDATE_CHROME_PIP_ENABLED,
  UPDATE_FILLER_WORD_HIGHLIGHT,
  UPDATE_FILLER_WORDS,
  UPDATE_FROM_PUBLIC_SHARE_PAGE,
  UPDATE_IS_FILLER_REMOVAL_FTUX,
  UPDATE_IS_INLINE_EMBED_ON_LOOM,
  UPDATE_IS_TRIMMING_FILLER_WORDS,
  UPDATE_PIP_SUPPORTED,
  UPDATE_SAFARI_PIP_ENABLED,
  UPDATE_SHARE_VIDEO_IN_UPLOAD_MODE,
  UPDATE_TRANSCRIPT_STATUS_ON_PLAYER,
  UPDATE_TRANSCRIPT_VISIBLE,
  UPDATE_VIDEO_FRAME_HEIGHT,
  UPDATE_VIDEO_PLAYER_ACTIVE_COMMENT_BUCKET_INDEX,
  UPDATE_VIDEO_PLAYER_AUTOPLAY,
  UPDATE_VIDEO_PLAYER_BUCKET_ID,
  UPDATE_VIDEO_PLAYER_BUFFERED_PARTS,
  UPDATE_VIDEO_PLAYER_COMMENT_MARKERS,
  UPDATE_VIDEO_PLAYER_CURRENT_TIME,
  UPDATE_VIDEO_PLAYER_DIMENSIONS,
  UPDATE_VIDEO_PLAYER_DURATION,
  UPDATE_VIDEO_PLAYER_EMOJI_BAR_HIDE_REACTION,
  UPDATE_VIDEO_PLAYER_ENDED,
  UPDATE_VIDEO_PLAYER_ENTER_FULL_SCREEN,
  UPDATE_VIDEO_PLAYER_FLIPPED_CAMERA,
  UPDATE_VIDEO_PLAYER_FROM_LINK_EXPAND,
  UPDATE_VIDEO_PLAYER_HOVERED_COMMENT_BUCKET_INDEX,
  UPDATE_VIDEO_PLAYER_IN_ANONYMOUS_REACTION_MODE,
  UPDATE_VIDEO_PLAYER_IN_COMMENT_MODE,
  UPDATE_VIDEO_PLAYER_LEAVE_FULL_SCREEN,
  UPDATE_VIDEO_PLAYER_LOCAL_REACTIONS,
  UPDATE_VIDEO_PLAYER_MOUSE_ACTIVE,
  UPDATE_VIDEO_PLAYER_MOUSE_INACTIVE,
  UPDATE_VIDEO_PLAYER_MUTE,
  UPDATE_VIDEO_PLAYER_NAME,
  UPDATE_VIDEO_PLAYER_NEEDS_PASSWORD,
  UPDATE_VIDEO_PLAYER_NOT_ENDED,
  UPDATE_VIDEO_PLAYER_NOT_WAITING,
  UPDATE_VIDEO_PLAYER_OWNER,
  UPDATE_VIDEO_PLAYER_PARENT_LOCATION,
  UPDATE_VIDEO_PLAYER_PASSWORD,
  UPDATE_VIDEO_PLAYER_PAUSED,
  UPDATE_VIDEO_PLAYER_PLAIN_SOURCE_URL,
  UPDATE_VIDEO_PLAYER_PLAY,
  UPDATE_VIDEO_PLAYER_PLAYBACK_RATE,
  UPDATE_VIDEO_PLAYER_PLAYING,
  UPDATE_VIDEO_PLAYER_PLAYING_PAUSED,
  UPDATE_VIDEO_PLAYER_POSTER_URL,
  UPDATE_VIDEO_PLAYER_REACTIONS_LOADED,
  UPDATE_VIDEO_PLAYER_REACTIONS_REMOVE,
  UPDATE_VIDEO_PLAYER_SEEKING,
  UPDATE_VIDEO_PLAYER_SHOW_COMMENTS,
  UPDATE_VIDEO_PLAYER_SOURCE,
  UPDATE_VIDEO_PLAYER_SOURCE_FROM_CLIP,
  UPDATE_VIDEO_PLAYER_THEATER_MODE,
  UPDATE_VIDEO_PLAYER_UNMUTE,
  UPDATE_VIDEO_PLAYER_VIDEO,
  UPDATE_VIDEO_PLAYER_VOLUME,
  UPDATE_VIDEO_PLAYER_VOLUME_CONTROL_ACTIVE,
  UPDATE_VIDEO_PLAYER_WAITING,
  UPDATE_VIDEO_PROCESSING_ERROR,
  UPDATE_VIDEO_VIEWER_NEEDS_PERMISSION,
  UPDATE_WAVEFORM_GENERATION_DETAILS,
  UPDATE_WAVEFORM_GENERATION_STATUS,
  UPLOAD_VIDEO_PROGRESS,
  VIDEO_PLAYER_EMOJI_SELECTED,
  VIDEO_PLAYER_EMOJI_SELECTED_RESET,
  VIDEO_PLAYER_EMOJI_SELECTED_SAVED,
  VIDEO_READY_TO_PLAY_ON_SHARE_PAGE,
} from '../constants/actions';

// state that gets reset when refetching new video source URLs
const DEFAULT_VIDEO_STATE = {
  buffered_parts: [],
  current_time: 0,
  ended: false,
  full_screen: false,
  has_started: false,
  muted: false,
  paused: true,
  playback_rate: 1.0,
  volume: 1.0,
  waiting: true,
  waveform_generation: null,
  waveform_url: null,
};

const DEFAULT_CAPTIONS_DETAILS_STATE = {
  captionsUrl: null,
  captionsSourceUrl: null,
};

const DEFAULT_VIDEO_PLAYER_STATE = {
  // analytics state
  analytics: {
    full_screen_enabled: false,
    playback_rate: 1.0,
    theater_mode_enabled: false,
  },

  // upload progress
  in_upload_mode: false,
  video_upload_progress: 0,

  // model state
  comment_markers: [],
  active_comment_bucket_index: null,
  hovered_comment_bucket_index: -1,
  fetch_error: null,
  flipped_camera: false,
  is_fetching: false, // fetching the video model or source URLs
  is_source_requested: false, // source URL has been requested already?
  needs_password: false,
  viewer_needs_permission: false,
  password: null,
  poster_url: null,
  source_mime_type: null,
  source_part_credentials: {},
  source_url: null,
  plainSourceUrl: null,
  sourceUrlIsTranscoded: false,
  video: {
    processing_information: {},
  },
  volume_control_active: false,

  // Emoji reactions
  // Local reactions are kept until page load to avoid breaking animations while it's running
  reactions: [], // server-side reactions (ie. not made during this session)
  local_reactions: [], // client-side reactions (ie. made this session)
  animate_reactions: [], // which of the local_reactions should animate on mount
  hide_reaction: null, // Emoji type that user just clicked. Hides it in the emoji bar

  // whether the mouse has moved over the player recently
  mouse_active: true,

  // whether the player is being loaded on the public share page
  from_public_share_page: false,

  // whether the player is an embed on a different loom video page(ie inline comment etc)
  is_inline_embed_on_loom: false,

  // whether the player is an embed inside Slack desktop app for knowing
  // if we need to kick off the shared auth workflow.
  is_from_slack_desktop_app: false,

  // the parent location this video was embedded on (used for analytics)
  // this can come from both link expanding (?from_url) or the public share page
  // util
  parent_location: null,

  // whether this video was used within a link expansion from the extension
  from_link_expand: false,

  // video state
  ...DEFAULT_VIDEO_STATE,
  autoplay: false,
  duration: 0,
  height: 0,
  width: 0,

  // control bar state
  seeking: false,

  // requests to change the video state
  //
  // NOTE: we should never rely on the video state in redux to be a single point
  //       of truth for manipulating the video player
  //       https://www.loom.com/share/d72e21e204e04bd6a592dacb14c89905
  request_current_time: null,
  request_enter_full_screen: false,
  request_leave_full_screen: false,
  request_mute: false,
  request_pause: false,
  request_play: false,
  request_playback_rate: null,
  request_unmute: false,
  request_volume: null,

  theater_mode: false,

  pipSupported: false,
  chromePipEnabled: false,
  safariPipEnabled: false,
  transcriptVisible: false,

  in_comment_mode: false,
  resume_after_comment: false,
  show_all_comments: false,
  scroll_to_bucket: null,
  resume_after_comment_overlay: false,

  videoReadyToPlayOnSharePage: false,
  emojiClickedInVideoPlayer: false,
  in_anonymous_reaction_mode: false,
  videoFrameHeight: 0,

  firstPlay: true,
  firstPlayAfterSeeking: false,
  captionsDetails: DEFAULT_CAPTIONS_DETAILS_STATE,
  currentCaption: null,
  captionsVisible: false,
  transcriptStatus: null,
  fillerWords: {},
  fillerWordHighlight: false,
  fillerWordRemoval: NONE,
  isFillerRemovalFTUX: false,
  isTrimmingFillerWords: false,
};

const videoPlayerReducer = (state = DEFAULT_VIDEO_PLAYER_STATE, action) => {
  let newState = state;

  switch (action.type) {
    case FETCH_VIDEO_PLAYER_VIDEO:
      newState = {
        ...state,
        is_fetching: true,
        is_source_requested: true,
        videoReadyToPlayOnSharePage: false,
      };
      break;
    case REQUEST_VIDEO_PLAYER_CURRENT_TIME:
      newState = {
        ...state,
        request_current_time: action.currentTime,
      };
      break;
    case REQUEST_VIDEO_PLAYER_ENTER_FULL_SCREEN:
      newState = {
        ...state,
        request_enter_full_screen: true,
        transcriptVisible: false,
      };
      break;
    case REQUEST_VIDEO_PLAYER_LEAVE_FULL_SCREEN:
      newState = {
        ...state,
        request_leave_full_screen: true,
      };
      break;
    case REQUEST_VIDEO_PLAYER_MUTE:
      newState = {
        ...state,
        request_mute: true,
      };
      break;
    case REQUEST_VIDEO_PLAYER_PAUSE:
      newState = {
        ...state,
        request_pause: true,
        request_play: false,
      };
      break;
    case REQUEST_VIDEO_PLAYER_PLAY:
      newState = {
        ...state,
        request_pause: false,
        request_play: true,
        firstPlayAfterSeeking: action.playAfterSeek,
      };
      break;
    case REQUEST_VIDEO_PLAYER_PLAYBACK_RATE:
      newState = {
        ...state,
        request_playback_rate: action.playbackRate,
      };
      break;
    case REQUEST_VIDEO_PLAYER_UNMUTE:
      newState = {
        ...state,
        request_unmute: true,
      };
      break;
    case REQUEST_VIDEO_PLAYER_VOLUME:
      newState = {
        ...state,
        request_volume: Math.max(0, Math.min(1, action.volume)),
      };
      break;
    case UPDATE_FROM_PUBLIC_SHARE_PAGE:
      newState = {
        ...state,
        from_public_share_page: action.fromPublicSharePage,
      };
      break;
    case UPDATE_IS_INLINE_EMBED_ON_LOOM:
      newState = {
        ...state,
        is_inline_embed_on_loom: action.isInlineEmbedOnLoom,
      };
      break;
    case UDPATE_IS_FROM_SLACK_DESKTOP_APP:
      newState = {
        ...state,
        is_from_slack_desktop_app: action.isFromSlackDesktopApp,
      };
      break;
    case UPDATE_VIDEO_PLAYER_AUTOPLAY:
      newState = {
        ...state,
        autoplay: action.autoplay,
      };
      break;
    case UPDATE_VIDEO_PLAYER_BUFFERED_PARTS:
      newState = {
        ...state,
        buffered_parts: action.bufferedParts,
      };
      break;
    case UPDATE_VIDEO_PLAYER_COMMENT_MARKERS:
      newState = {
        ...state,
        comment_markers: action.commentMarkers,
      };
      break;
    case UPDATE_VIDEO_PLAYER_ACTIVE_COMMENT_BUCKET_INDEX:
      newState = {
        ...state,
        active_comment_bucket_index: action.bucketIndex,
      };
      break;
    case UPDATE_VIDEO_PLAYER_HOVERED_COMMENT_BUCKET_INDEX:
      newState = {
        ...state,
        hovered_comment_bucket_index: action.bucketIndex,
      };
      break;
    case UPDATE_VIDEO_PLAYER_CURRENT_TIME:
      newState = {
        ...state,
        current_time: action.current_time,
        request_current_time: null,
      };
      break;
    case UPDATE_VIDEO_PLAYER_DIMENSIONS:
      newState = {
        ...state,
        height: action.height,
        width: action.width,
      };
      break;
    case UPDATE_VIDEO_PLAYER_DURATION:
      newState = {
        ...state,
        duration: action.duration,
      };
      break;
    case UPDATE_VIDEO_PLAYER_ENDED:
      newState = {
        ...state,
        ended: true,
        paused: true,
        request_pause: false,
        in_anonymous_reaction_mode: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_NOT_ENDED:
      newState = {
        ...state,
        ended: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_ENTER_FULL_SCREEN:
      newState = {
        ...state,
        analytics: {
          ...state.analytics,
          full_screen_enabled: true,
        },
        full_screen: true,
        request_enter_full_screen: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_FROM_LINK_EXPAND:
      newState = {
        ...state,
        from_link_expand: action.fromLinkExpand,
      };
      break;
    case UPDATE_VIDEO_PLAYER_FLIPPED_CAMERA:
      newState = {
        ...state,
        flipped_camera: action.flippedCamera,
      };
      break;
    case UPDATE_VIDEO_PLAYER_LEAVE_FULL_SCREEN:
      newState = {
        ...state,
        full_screen: false,
        request_leave_full_screen: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_MOUSE_ACTIVE:
      newState = {
        ...state,
        mouse_active: true,
      };
      break;
    case UPDATE_VIDEO_PLAYER_MOUSE_INACTIVE:
      newState = {
        ...state,
        mouse_active: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_MUTE:
      newState = {
        ...state,
        muted: true,
        request_mute: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_NAME:
      newState = { ...state, video: { ...state.video, name: action.name } };
      break;
    case UPDATE_VIDEO_PLAYER_NEEDS_PASSWORD:
      newState = {
        ...state,
        needs_password: action.needsPassword,
      };
      break;
    case UPDATE_VIDEO_PLAYER_THEATER_MODE:
      newState = {
        ...state,
        theater_mode: action.theaterMode,
        analytics: {
          ...state.analytics,
          theater_mode_enabled: action.theaterMode,
        },
      };
      break;
    case UPDATE_VIDEO_VIEWER_NEEDS_PERMISSION:
      newState = {
        ...state,
        viewer_needs_permission: action.needsPermission,
      };
      break;
    case UPDATE_VIDEO_PLAYER_NOT_WAITING:
      newState = {
        ...state,
        waiting: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_OWNER:
      newState = {
        ...state,
        video: {
          ...state.video,
          owner_full_name: action.displayName,
          owner_avatar: action.avatar,
        },
      };
      break;
    case UPDATE_VIDEO_PLAYER_PARENT_LOCATION:
      newState = {
        ...state,
        parent_location: action.parent_location,
      };
      break;
    case UPDATE_VIDEO_PLAYER_PASSWORD:
      newState = {
        ...state,
        password: action.password,
      };
      break;
    case UPDATE_VIDEO_PLAYER_PAUSED:
      newState = {
        ...state,
        paused: true,
        request_pause: false,
        firstPlay: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_PLAY:
      newState = {
        ...state,
        ended: false,
        paused: false,
        request_play: false,
        request_pause: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_PLAYING:
      newState = {
        ...state,
        has_started: true,
        waiting: false,
        firstPlayAfterSeeking: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_PLAYBACK_RATE:
      newState = {
        ...state,
        analytics: {
          ...state.analytics,
          playback_rate: action.playbackRate,
        },
        playback_rate: action.playbackRate,
        request_playback_rate: null,
      };
      break;
    case UPDATE_VIDEO_PLAYER_POSTER_URL:
      newState = {
        ...state,
        poster_url: action.posterUrl,
      };
      break;
    case UPDATE_VIDEO_PLAYER_SEEKING:
      newState = {
        ...state,
        seeking: action.seeking,
      };
      break;
    case UPDATE_VIDEO_PLAYER_SOURCE:
      newState = {
        ...state,
        ...DEFAULT_VIDEO_STATE,
        playback_rate: state.playback_rate,
        is_fetching: false,
        source_part_credentials: action.partCredentials,
        source_mime_type: action.sourceMimeType,
        source_url: action.sourceUrl,
        sourceUrlIsTranscoded: action.sourceUrlIsTranscoded || false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_SOURCE_FROM_CLIP:
      newState = {
        ...state,
        ...DEFAULT_VIDEO_STATE,
        muted: state.muted,
        playback_rate: state.playback_rate,
        paused: state.paused,
        has_started: state.has_started,
        is_fetching: false,
        source_part_credentials: action.partCredentials,
        source_mime_type: action.sourceMimeType,
        source_url: action.sourceUrl,
        sourceUrlIsTranscoded: action.sourceUrlIsTranscoded || false,
        autoplay: action.autoplay,
        request_play: action.requestPlay,
      };

      if (action.seekTime != null) {
        newState.request_current_time = action.seekTime;
      }

      break;
    case UPDATE_VIDEO_PLAYER_UNMUTE:
      newState = {
        ...state,
        muted: false,
        request_unmute: false,
      };
      break;
    case UPDATE_VIDEO_PLAYER_VIDEO:
      newState = {
        ...state,
        is_fetching: false,
        video: prioritizeSignedThumbnails(action.video),
      };
      break;

    case UPDATE_VIDEO_PROCESSING_ERROR:
      newState = {
        ...state,
        video: {
          ...state.video,
          processing_information: {
            ...state.video.processing_information,
            videoUploadValid: action.videoUploadValid,
            videoUploadMessage: action.videoUploadMessage,
          },
        },
      };
      break;

    case updateVideoPlayerVideoRecordingVersion.toString():
      newState = {
        ...state,
        video: {
          ...state.video,
          video_properties: {
            ...state.video.video_properties,
            recording_version: action.payload,
          },
        },
      };
      break;

    case UPDATE_VIDEO_PLAYER_VOLUME:
      newState = {
        ...state,
        request_volume: null,
        volume: action.volume,
      };
      break;
    case UPDATE_VIDEO_PLAYER_VOLUME_CONTROL_ACTIVE:
      newState = {
        ...state,
        volume_control_active: action.active,
      };
      break;
    case UPDATE_VIDEO_PLAYER_WAITING:
      newState = {
        ...state,
        waiting: true,
      };
      break;
    case UPDATE_VIDEO_PLAYER_LOCAL_REACTIONS:
      newState = {
        ...state,
        local_reactions: action.reactions,
      };
      break;
    case UPDATE_VIDEO_PLAYER_REACTIONS_LOADED:
      newState = {
        ...state,
        reactions: action.reactions,
      };
      break;
    case UPDATE_VIDEO_PLAYER_REACTIONS_REMOVE:
      newState = {
        ...state,
        reactions: state.reactions.filter(r => r.id != action.id),
        local_reactions: state.local_reactions.filter(r => r.id != action.id),
      };
      break;
    case UPDATE_VIDEO_PLAYER_EMOJI_BAR_HIDE_REACTION:
      newState = {
        ...state,
        hide_reaction: action.reaction || null,
      };
      break;
    case VIDEO_PLAYER_EMOJI_SELECTED:
      newState = {
        ...state,
        hide_reaction: action.hideReaction,
        animate_reactions: action.ids,
        local_reactions: [...state.local_reactions, action.reaction],
      };
      break;
    case VIDEO_PLAYER_EMOJI_SELECTED_SAVED:
      newState = {
        ...state,
        local_reactions: state.local_reactions.map(r => {
          return r.localId === action.localId ? { ...r, id: action.id } : r;
        }),
      };
      break;
    case VIDEO_PLAYER_EMOJI_SELECTED_RESET:
      newState = {
        ...state,
        hide_reaction: null,
        animate_reactions: [],
      };
      break;
    case UPDATE_SHARE_VIDEO_IN_UPLOAD_MODE:
      newState = { ...state, in_upload_mode: action.inUploadMode };
      break;
    case UPDATE_VIDEO_PLAYER_IN_COMMENT_MODE:
      newState = {
        ...state,
        in_comment_mode: action.inCommentMode,
        resume_after_comment: action.resumeAfterComment,
      };
      break;
    case UPDATE_VIDEO_PLAYER_IN_ANONYMOUS_REACTION_MODE:
      newState = {
        ...state,
        in_anonymous_reaction_mode: action.inAnonymousReactionMode,
      };
      break;
    case UPDATE_VIDEO_PLAYER_BUCKET_ID:
      newState = {
        ...state,
        scroll_to_bucket: action.bucketId,
      };
      break;
    case UPDATE_VIDEO_PLAYER_SHOW_COMMENTS:
      newState = {
        ...state,
        show_all_comments: action.showAllComments,
        scroll_to_bucket: action.bucketId,
        resume_after_comment_overlay: action.resumeOnExit,
      };
      break;
    case UPLOAD_VIDEO_PROGRESS:
      newState = {
        ...state,
        video_upload_progress: action.progress,
      };
      break;
    case REQUEST_VIDEO_PLAYER_SEEKTO_TS:
      newState = {
        ...state,
        request_current_time: action.ts,
        paused: true,
        has_started: true,
        waiting: false,
      };
      break;

    case UPDATE_PIP_SUPPORTED:
      newState = {
        ...state,
        pipSupported: action.pipSupported,
      };
      break;

    case UPDATE_CHROME_PIP_ENABLED:
      newState = {
        ...state,
        chromePipEnabled: action.chromePipEnabled,
      };
      break;

    case UPDATE_SAFARI_PIP_ENABLED:
      newState = {
        ...state,
        safariPipEnabled: action.safariPipEnabled,
      };
      break;

    case UPDATE_VIDEO_PLAYER_PLAYING_PAUSED:
      newState = {
        ...state,
        request_pause: true,
        request_play: true,
      };
      break;

    case UPDATE_TRANSCRIPT_VISIBLE:
      newState = {
        ...state,
        transcriptVisible: action.transcriptVisible,
      };
      break;

    case UPDATE_CAPTIONS_VISIBLE:
      newState = {
        ...state,
        captionsVisible: action.captionsVisible,
      };
      break;

    case UPDATE_TRANSCRIPT_STATUS_ON_PLAYER:
      newState = {
        ...state,
        transcriptStatus: action.transcriptStatus,
      };
      break;

    case VIDEO_READY_TO_PLAY_ON_SHARE_PAGE:
      newState = {
        ...state,
        videoReadyToPlayOnSharePage: true,
      };
      break;

    case EMOJI_CLICKED_IN_VIDEO_PLAYER:
      newState = {
        ...state,
        emojiClickedInVideoPlayer: action.emojiClickedInVideoPlayer,
      };
      break;

    case UPDATE_VIDEO_FRAME_HEIGHT:
      newState = {
        ...state,
        videoFrameHeight: action.videoFrameHeight,
      };
      break;

    case UPDATE_CAPTIONS_DETAILS:
      newState = {
        ...state,
        captionsDetails: action.captionsDetails,
      };
      break;

    case SET_CURRENT_CAPTION:
      newState = {
        ...state,
        currentCaption: action.caption,
      };
      break;
    case UPDATE_FILLER_WORDS:
      newState = {
        ...state,
        fillerWords: action.fillerWords,
        fillerWordRemoval: action.fillerWordRemoval,
      };
      break;
    case UPDATE_FILLER_WORD_HIGHLIGHT:
      newState = {
        ...state,
        fillerWordHighlight: action.fillerWordHighlight,
      };
      break;
    case UPDATE_IS_FILLER_REMOVAL_FTUX:
      newState = {
        ...state,
        isFillerRemovalFTUX: action.isFillerRemovalFTUX,
      };
      break;
    case UPDATE_IS_TRIMMING_FILLER_WORDS:
      newState = {
        ...state,
        isTrimmingFillerWords: action.isTrimmingFillerWords,
      };
      break;
    case UPDATE_VIDEO_PLAYER_PLAIN_SOURCE_URL:
      newState = {
        ...state,
        plainSourceUrl: action.plainSourceUrl,
      };

      break;
    case UPDATE_WAVEFORM_GENERATION_DETAILS:
      newState = {
        ...state,
        video: {
          ...state.video,
          waveform_generation: action.status,
          waveform_url: action.url,
        },
      };
      break;
    case UPDATE_WAVEFORM_GENERATION_STATUS:
      newState = {
        ...state,
        video: {
          ...state.video,
          waveform_generation: action.status,
        },
      };
      break;

    default:
      newState = state;
  }

  newState.isMainVideoOnSharePage =
    newState.from_public_share_page && !newState.is_inline_embed_on_loom;

  newState.controlsActive =
    newState.has_started &&
    (newState.ended ||
      newState.paused ||
      newState.seeking ||
      newState.mouse_active);

  newState.userCanAccessTranscript =
    newState.video?.current_user_is_owner ||
    ((!newState.video?.is_protected ||
      Boolean(newState.video?.is_protected && newState.password)) &&
      newState.video?.show_transcript_to_viewer);

  // We should reprocess the transcript for filler words if the transcript
  // was created before the filler word removal feature for this user.
  // shouldReprocessTranscriptForFillerWords checks that:
  // User should be the current owner of the video,
  // and
  // If the fillerWords object does not have the key
  // NUM_FILLER_WORDS_IN_VIDEO_VERSION then the transcript was created
  // prior to the Filler word removal feature. We cannot check
  // !fillerWords[TRANSCRIPTION.NUM_FILLER_WORDS_IN_VIDEO_VERSION]
  // as there are cases where
  // fillerWords[TRANSCRIPTION.NUM_FILLER_WORDS_IN_VIDEO_VERSION]
  // would be 0 and then we would not want to reprocess the transcript.
  // Hence we check if the key exists on the object.
  // and
  // Transcript Status is Success
  newState.shouldReprocessTranscriptForFillerWords =
    newState.video?.current_user_is_owner &&
    !Object.prototype.hasOwnProperty.call(
      newState.fillerWords,
      NUM_FILLER_WORDS_IN_VIDEO_VERSION
    ) &&
    newState.transcriptStatus === SUCCESS;

  return newState;
};

export default videoPlayerReducer;
